/**
 * CRM Connection Status (Phase 1 smoke test)
 *
 * Minimal foundation screen: confirms the CRM module boots, reports module
 * configuration (/api/crm/health), and — for super_admins — the read-only
 * leads connection (/api/crm/leads/verify): can we read crm_lead_export, is the
 * role read-only, and which study_slug filter is active. Replaced by the real
 * contacts/timeline UI in Phase 3.
 */
import { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, Loader2, Database, ShieldCheck, Activity } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { Alert } from '../components/ui';
import PageHeader from '../components/PageHeader';

interface HealthResp {
  ok: boolean;
  configured: boolean;
  missing: string[];
}

interface LeadsVerifyResp {
  ok: boolean;
  canReadExport: boolean;
  readOnly: boolean;
  leadsWriteGrants: string[];
  exportColumnCount: number;
  exportColumns: string[];
  testSlugFilter: string | null;
}

function Dot({ ok }: { ok: boolean }) {
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`} />;
}

function Row({ ok, label, value }: { ok: boolean; label: string; value?: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
        <Dot ok={ok} />
        {label}
      </span>
      {value !== undefined && (
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{value}</span>
      )}
    </div>
  );
}

export default function ConnectionStatus() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [health, setHealth] = useState<HealthResp | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const [leads, setLeads] = useState<LeadsVerifyResp | null>(null);
  const [leadsErr, setLeadsErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const h = await api.get('/api/crm/health');
        if (active) setHealth(h.data);
      } catch (e: any) {
        if (active) setHealthErr(e?.response?.data?.error || e.message);
      }
      if (isSuperAdmin) {
        try {
          const l = await api.get('/api/crm/leads/verify');
          if (active) setLeads(l.data);
        } catch (e: any) {
          if (active) setLeadsErr(e?.response?.data?.detail || e?.response?.data?.error || e.message);
        }
      }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [isSuperAdmin]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader icon={Activity} title="System" />

      {/* Module configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <Database className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            Module configuration
          </h2>
        </div>
        {healthErr ? (
          <Alert tone="error" className="mt-4">
            <span>Failed to reach the CRM API: {healthErr}</span>
          </Alert>
        ) : health ? (
          <div className="mt-2">
            <Row ok={health.configured} label="CRM environment configured" value={health.configured ? 'Yes' : 'No'} />
            {!health.configured && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Missing: {health.missing.join(', ')}. CRM features are degraded; the rest of the platform is unaffected.</span>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Leads connection — super admin only */}
      {isSuperAdmin && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 dark:bg-gray-800 dark:border-gray-700">
          <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-700">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
              <ShieldCheck className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              WCR leads connection
            </h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
              read-only
            </span>
          </div>
          {leadsErr ? (
            <Alert tone="error" className="mt-4">
              <span>{leadsErr}</span>
            </Alert>
          ) : leads ? (
            <div className="mt-2">
              <Row ok={leads.canReadExport} label="Can read crm_lead_export" value={leads.canReadExport ? 'Yes' : 'No'} />
              <Row ok={leads.readOnly} label="Connection is read-only (no write grants on public.leads)" value={leads.readOnly ? 'Yes' : 'No'} />
              <Row ok={leads.exportColumnCount > 0} label="Columns visible in crm_lead_export" value={String(leads.exportColumnCount)} />
              <Row ok={!!leads.testSlugFilter} label="Test-only study_slug filter" value={leads.testSlugFilter || 'none (production)'} />
              {leads.leadsWriteGrants.length > 0 && (
                <Alert tone="error" className="mt-3">
                  <span>Unexpected write grants on public.leads: {leads.leadsWriteGrants.join(', ')}</span>
                </Alert>
              )}
            </div>
          ) : null}
        </div>
      )}

      {!isSuperAdmin && (
        <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          CRM module loaded.
        </p>
      )}
    </div>
  );
}
